import { serializeMessageFragment } from '../../utils/serialization/serializeMessageFragment';
import { splitPayloadIntoFragments } from '../../utils/helpers/splitPayloadIntoFragments';
import { createSendInstruction } from './createSendInstruction';
import { MessageFragment } from '../../types/messageFragment';
import { MAX_CHUNK_CONTENT_SIZE } from '../../constants/chunk';

import { Keypair, TransactionInstruction } from '@solana/web3.js';
import { SenderHmacProofInput } from "./../../types/crypto/senderHmacProofInput";

/**
 * Splits the given payload into fragments, serializes each,
 * and sends each as a separate transaction to the Unlink program.
 *
 * This function ensures large messages are broken up and reliably transmitted.
 *
 * @param payer - Keypair used to fund and sign transactions
 * @param senderViewPublicKey - Long-term sender view public key
 * @param senderViewPrivateKey - Corresponding private key
 * @param senderNextViewPublicKey - Forward secrecy view key (or zero-filled)
 * @param receiverViewPublicKey - Recipient's view public key
 * @param messagePayload - Raw binary message content to send (pre-fragmentation)
 * @param spamMitigationFeeLamports - Lamports paid for spam deterrence
 * @param conversationId - Thread ID that the message belongs to
 * @param messageType - Type of message (0 = plain text, ≥128 = custom)
 * @param clientId - Optional client identifier (defaults to all zeroes)
 * @returns Promise resolving to an array of Solana TransactionInstructions (one per message fragment)
 */
export async function createChunkedSendInstructions({
  payer,
  senderViewPublicKey,
  senderViewPrivateKey,
  senderNextViewPublicKey,
  receiverViewPublicKey,
  messagePayload,
  spamMitigationFeeLamports,
  conversationId,
  messageType,
  clientId = new Uint8Array(8),
  kyberSecret,
  randomFill,
  createSenderHmacProof,
  filterTag,
}: {
  payer: Keypair;
  senderViewPublicKey: Uint8Array;
  senderViewPrivateKey: Uint8Array;
  senderNextViewPublicKey: Uint8Array;
  receiverViewPublicKey: Uint8Array;
  messagePayload: Uint8Array;
  spamMitigationFeeLamports: bigint;
  conversationId: Uint8Array;
  messageType: number;
  clientId?: Uint8Array;
  kyberSecret?: Uint8Array;
  randomFill: (target: Uint8Array) => void;
  createSenderHmacProof: (input: SenderHmacProofInput) => Promise<Uint8Array>;
  filterTag: Uint8Array;
}): Promise<{
  instructions: TransactionInstruction[];
  messageId: Uint8Array;
}> {
  const instructions: TransactionInstruction[] = [];

  // Generate a unique message ID (8 bytes) to identify all fragments as part of a single logical message.
  const messageId = crypto.getRandomValues(new Uint8Array(8));

  // Break the payload into fixed-size chunks that fit on-chain per Solana constraints.
  const fragmentPayloads = splitPayloadIntoFragments(messagePayload, MAX_CHUNK_CONTENT_SIZE);
  const totalFragments = fragmentPayloads.length;

  for (let i = 0; i < totalFragments; i++) {
    // Construct metadata for this individual fragment
    const fragment: MessageFragment = {
      version: 1,
      messageId,
      fragmentIndex: i,
      totalFragments,
      messageType,
      clientId,
      payload: fragmentPayloads[i],
    };

    // Serialize the fragment into a binary format suitable for on-chain instruction data.
    const serialized = serializeMessageFragment(fragment);

    // Create an instruction to send this specific fragment
    const instruction = await createSendInstruction({
      payer,
      senderViewPublicKey,
      senderViewPrivateKey,
      senderNextViewPublicKey,
      receiverViewPublicKey,
      messagePayload: serialized,
      spamMitigationFeeLamports,
      conversationId,
      kyberSecret,
      randomFill,
      createSenderHmacProof,
      filterTag,
    }); 

    // Append the resulting instruction to the final batch.
    instructions.push(instruction);
  }

  // Return the full set of instructions to be submitted in a transaction or batched manually.
  return {
    instructions,
    messageId,
  }
}